import type {MatchParams} from '@wandb/weave/common/types/base';
import {truncate} from 'lodash';

import {isSensitiveUrlParam} from '../../../util/sensitiveFieldKeys';
import type {ContentPart} from '../store/thread';
import {collectAgentPageContext} from './collect';

/**
 * PromptContextParams describes the user's current route scope (entity,
 * project, run, normalized path, and query) that React Router captures. The
 * runtime hook consumes this on every user message to derive
 * wandb_web_page_state.
 */
export type PromptContextParams = MatchParams & {
  /**
   * Current path in the app's router. Unlike `url`, this is stable across
   * standalone wandb.ai and Forge (shell prefix stripped client-side).
   */
  appPath?: string;
  /** Current query string in the app's router, including the leading `?`. */
  search?: string;
  /** Stable identity of the product serving the current Forge page. */
  productId?: string;
};

/**
 * buildPromptContextParts produces the wandb-web-page-state content part
 * describing the user's current page (URL, entity/project, and any captured
 * page content) at send time.
 */
export function buildPromptContextParts({
  entityName,
  projectName,
  appPath,
  search,
  productId,
}: PromptContextParams): ContentPart[] {
  const pageContent = collectAgentPageContext();
  const hasProject = !!(entityName && projectName);
  return [
    {
      type: 'wandb-web-page-state',
      content: pageContent.length > 0 ? pageContent : undefined,
      entityName: entityName || undefined,
      projectName: hasProject ? projectName : undefined,
      productId,
      appPath: appPath || undefined,
      // NOTE: url omits the query and hash. queryParams holds the query
      // with sensitive keys removed.
      url: window.location.origin + window.location.pathname,
      queryParams: collectQueryParams(search),
    },
  ];
}

const QUERY_PARAM_VALUES_MAX = 32;
const QUERY_PARAM_KEY_LENGTH_MAX = 64;
const QUERY_PARAM_VALUE_LENGTH_MAX = 512;

/**
 * collectQueryParams returns the page's query params with sensitive keys
 * removed, or undefined when none remain. Repeated keys become arrays in
 * encounter order. Every value counts toward QUERY_PARAM_VALUES_MAX, and a
 * presence-only param such as `?published` keeps an empty value.
 */
function collectQueryParams(
  search: string | undefined
): Record<string, string | string[]> | undefined {
  const entries = Array.from(new URLSearchParams(search))
    .filter(([key]) => isAllowedQueryParam(key))
    .slice(0, QUERY_PARAM_VALUES_MAX);
  if (entries.length === 0) {
    return undefined;
  }

  const grouped = new Map<string, string[]>();
  for (const [key, value] of entries) {
    const values = grouped.get(key) ?? [];
    values.push(truncate(value, {length: QUERY_PARAM_VALUE_LENGTH_MAX}));
    grouped.set(key, values);
  }

  return Object.fromEntries(
    Array.from(grouped, ([key, values]) => [
      key,
      values.length === 1 ? values[0] : values,
    ])
  );
}

function isAllowedQueryParam(key: string): boolean {
  return (
    key !== '' &&
    key.length <= QUERY_PARAM_KEY_LENGTH_MAX &&
    !isSensitiveUrlParam(key)
  );
}
